import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { ActivityCodeEnum, CategoryEnum, NotificationAreaLodgementTypeEnum, PurposeEnum } from 'src/app/models/shared-models';
import { SessionService } from 'src/app/services/session.service';
import { environment } from 'src/environments/environment';
import { IActivityStorageDataModel, ICategoryPurposeDataModel, ICoastalResponseMessages, INotificationAreaLimitModel, INotificationAreaModel, IPurposeModel, ISetAreaResponseModel } from '../models/veg-wizard-models';
import { Logger } from './logger-service';
import { NotificationAreaLimitService } from './notification-area-limit-service';

@Injectable({ providedIn: 'root' })
export class NotificationAreaService {
  private readonly http = inject(HttpClient);
  private readonly logger = inject(Logger);
  private readonly sessionService = inject(SessionService);
  private readonly notificationAreaLimitService = inject(NotificationAreaLimitService);

  sessionSetNotificationArea$(model: INotificationAreaModel): Observable<ISetAreaResponseModel> {
    const sessionId = this.sessionService.getSessionIdOrError();
    const apiModel = { sessionKey: sessionId, ...model };
    if (apiModel.howToProvide === NotificationAreaLodgementTypeEnum.manualUpload) {
      apiModel.coordinates = [];
      apiModel.lodgementShapeGeoJson = '';
      apiModel.lotPlanShapeGeoJson = '';
    }
    const url = `${environment.apiUrl}/session/set-area`;
    return this.http.post<ISetAreaResponseModel>(url, apiModel);
  }

  setIsCoastalResponse$(isCoastalResponse: boolean, sessionId: string): Observable<boolean> {
    const url = `${environment.apiUrl}/session/set-is-coastal-response`;
    const modelForPost = {
      sessionKey: sessionId,
      isCoastal: isCoastalResponse,
    };
    return this.http.post<boolean>(url, modelForPost);
  }

  requiresClearingAreaQuestion(purpose: IPurposeModel): boolean {
    if (purpose) return purpose.purposeCode === PurposeEnum.agriculturalEfficiency;
    return false;
  }

  checkIfClearingAreaQuestionIsValid(purpose: IPurposeModel, isLocatedInInvalidArea: boolean): boolean {
    return this.requiresClearingAreaQuestion(purpose) ? isLocatedInInvalidArea === false : true;
  }

  requiresIsCoastalResponse(selectedCategoryPurposes: ICategoryPurposeDataModel[], activity: IActivityStorageDataModel): boolean {
    if (!selectedCategoryPurposes || selectedCategoryPurposes.length === 0) {
      throw new Error('No categories have been selected for this session.');
    }
    const activityCode = activity ? activity.activity : null;
    const activitiesRequiringCoastalResponse = [ActivityCodeEnum.removeContaminant, ActivityCodeEnum.restoration, ActivityCodeEnum.other];
    const hasCoastalActivity = activitiesRequiringCoastalResponse.some((a) => activityCode === a);
    const hasCoastalCategoryPurpose = selectedCategoryPurposes.some((x) => x.requiresIsCoastalResponse === true);
    return hasCoastalActivity || hasCoastalCategoryPurpose;
  }

  isCoastalResponseValid(purpose: IPurposeModel, categoryModel: ICategoryPurposeDataModel[], activityModel: IActivityStorageDataModel, isCoastalResponse: boolean): boolean {
    const messages = this.getIsCoastalResponseMessage(purpose, categoryModel, activityModel, isCoastalResponse);
    return !messages.showFirebreakCoastalMessage;
  }

  getIsCoastalResponseMessage(purposeModel: IPurposeModel, categoryModel: ICategoryPurposeDataModel[], activityModel: IActivityStorageDataModel, isCoastalResponse: boolean): ICoastalResponseMessages {
    if (purposeModel == null) {
      throw new Error('No purpose selected for this session.');
    }
    const categories = categoryModel ?? ([] as ICategoryPurposeDataModel[]);
    const requiresResponse = this.requiresIsCoastalResponse(categories, activityModel);
    const activityCode = activityModel ? activityModel.activity : null;
    const requiresThinningResponse = categories.some((category: ICategoryPurposeDataModel) => category.categoryCode === CategoryEnum.catB) && purposeModel.purposeCode === PurposeEnum.thinning;
    const requiresFirebreakResponse = activityCode === ActivityCodeEnum.firebreaks && categories.some((category: ICategoryPurposeDataModel) => category.categoryCode === CategoryEnum.catB);

    return {
      showThinningCoastalMessage: requiresResponse && requiresThinningResponse && isCoastalResponse === true,
      showThinningNonCoastalMessage: requiresResponse && requiresThinningResponse && isCoastalResponse === false,
      showFirebreakCoastalMessage: requiresResponse && requiresFirebreakResponse && isCoastalResponse === true,
    };
  }

  getNotificationAreaLimitCap$(isCoastalSelected: boolean | null): Observable<INotificationAreaLimitModel> {
    return this.notificationAreaLimitService.getNotificationAreaLimit$([], isCoastalSelected);
  }

  getNotificationAreaLimitErrorMessage(limit: INotificationAreaLimitModel | null, purposeCode: string, isKoalaAreaValid: boolean, koalaClearingMessage: string | undefined): string {
    if (limit == null) return '';
    if (purposeCode === PurposeEnum.managingWeeds || purposeCode === PurposeEnum.managingRegrowthDensity) {
      if (!isKoalaAreaValid && !!koalaClearingMessage) return koalaClearingMessage;
    }
    if (limit != null) {
      return `The maximum area which can be cleared is ${this.formatHectares(limit.remainingAreaLimitInHectares)}.`;
    }
    return '';
  }

  private formatHectares(hectares: number): string {
    const hecatresToSquareMetersMulitplier = 10000;

    return hectares <= 1 ? `${hectares} hectares (${hectares * hecatresToSquareMetersMulitplier} m<sup><small>2</small></sup>)` : `${hectares} hectares`;
  }
}
